import { pick, hasPath, isNil, pathOr } from "ramda";
import { Maybe } from "ramda-fantasy";

import {
  formatNumber,
  maybeEmpty,
  normalizarString,
  zeroToString,
  removePad,
  escalaFormatacao,
} from "../../../../util";
import { ID_OPCAO_PADRAO_CASAS_DECIMAIS } from "../../../../model";

import Big from "big.js";

const onlyNumbers = number => (isNaN(number) ? Maybe.Nothing() : Maybe(number));

const normalize = str => normalizarString(String(str).trim());

const rawCellText = (getter, node, cellValue) => {
  const bruto = node && node.data ? getter({ data: node.data }) : null;

  if (bruto == null) {
    return cellValue;
  }

  return typeof bruto === "object"
    ? pathOr(cellValue, ["__metadados__", "valor"], bruto)
    : bruto;
};

const filterStringContains = (getter, filterValue, cellValue, node) =>
  Boolean(filterValue.match(/^\D/)) &&
  normalize(rawCellText(getter, node, cellValue)).indexOf(
    normalize(filterValue)
  ) >= 0;

const equalsTest = (getter, testeNumerico) => (filterValue, cellValue, node) =>
  filterStringContains(getter, filterValue, cellValue, node) ||
  testeNumerico(filterValue, cellValue);

const hasNonNumericalChars = value =>
  maybeEmpty(value)
    .map(String)
    .map(value => value.replace(/[,.%]/, ""))
    .map(value => /\D/.test(value))
    .getOrElse(false);

const parseInputNumber = value =>
  maybeEmpty(value)
    .map(String)
    .map(value => value.replace(/[^0-9,-]/g, "").replace(",", "."))
    .map(parseFloat)
    .chain(onlyNumbers);

const parseValueNumber = value =>
  maybeEmpty(value)
    .map(String)
    .map(value => value.replace(/[^0-9E.-]/g, ""))
    .map(parseFloat)
    .chain(onlyNumbers);

const numberComparator = getter => (a, b, nodeA, nodeB, isInverted) => {
  const numberA = parseValueNumber(a).getOrElse(null);
  const numberB = parseValueNumber(b).getOrElse(null);
  if (numberA != null && numberB != null) {
    return new Big(a).cmp(new Big(b));
  } else if (numberA != null && numberB == null) {
    return -1;
  } else if (numberA == null && numberB != null) {
    return +1;
  } else {
    return String(rawCellText(getter, nodeA, a) || "").localeCompare(
      String(rawCellText(getter, nodeB, b) || "")
    );
  }
};

const formatDecimalPlaces = (value, decimals) => {
  return decimals === ID_OPCAO_PADRAO_CASAS_DECIMAIS
    ? value
    : parseFloat(value.toFixed(decimals));
};

export const createNumberColumn = (getter, col, decimalsDefault, translate) => {
  const decimals =
    hasPath(["casasDecimais"], col) && !isNil(col.casasDecimais)
      ? col.casasDecimais
      : decimalsDefault;

  const escala = col.escala;

  const filtering = fn => (filterValue, cellValue) => {
    if (hasNonNumericalChars(filterValue)) {
      return false;
    }

    const filter = parseInputNumber(filterValue)
      .map(value => formatDecimalPlaces(value, decimals))
      .getOrElse(null);

    const value = parseValueNumber(cellValue)
      .map(value => formatDecimalPlaces(value, decimals))
      .getOrElse(null);

    return filter != null && value != null ? fn(filter, value) : false;
  };

  return {
    allowedAggFuncs: ["avg", "median", "first", "last", "max", "min", "sum"],
    aggFunc: "sum",
    filter: "formattedNumberFilter",
    floatingFilterComponent: "proxyFloatingFilter",
    filterParams: {
      filterOptions: [
        {
          displayKey: "equals",
          displayName: translate("ag-grid.equals"),
          test: equalsTest(
            getter,
            filtering((filter, cell) => filter === cell)
          ),
        },
        {
          displayKey: "greaterThan",
          displayName: translate("ag-grid.greaterThan"),
          test: filtering((filter, cell) => cell > filter),
        },
        {
          displayKey: "greaterThanOrEqual",
          displayName: translate("ag-grid.greaterThanOrEqual"),
          test: filtering((filter, cell) => cell >= filter),
        },
        {
          displayKey: "lessThan",
          displayName: translate("ag-grid.lessThan"),
          test: filtering((filter, cell) => cell < filter),
        },
        {
          displayKey: "lessThanOrEqual",
          displayName: translate("ag-grid.lessThanOrEqual"),
          test: filtering((filter, cell) => cell <= filter),
        },
      ],
      defaultOption: "equals",
    },
    comparator: numberComparator(getter),
    valueGetter: params =>
      maybeEmpty(
        typeof getter(params) === "object"
          ? getter(params)
          : getter(params) === ""
          ? ""
          : escalaFormatacao(zeroToString(Number(getter(params))), escala)
      ).getOrElse(""),
    valueFormatter: params =>
      maybeEmpty(params.value)
        .map(parseFloat)
        .chain(onlyNumbers)
        .map(number => number.toString())
        .map(number =>
          formatNumber(
            {
              ...pick(["decimals", "prefix", "suffix"], col),
              decimals,
            },
            number
          )
        )
        .getOrElse(typeof getter(params) === "undefined" ? 0 : getter(params)),
  };
};

export const createMoneyColumn = (getter, col, decimalsDefault, translate) => {
  const moneySymbol = code => {
    const moneySymbols = {
      BRL: "R$",
      EUR: "€",
      USD: "$",
    };

    return moneySymbols[code];
  };

  const decimals =
    hasPath(["casasDecimais"], col) && !isNil(col.casasDecimais)
      ? col.casasDecimais
      : decimalsDefault;

  const escala = col.escala;

  const filtering = fn => (filterValue, cellValue) => {
    if (hasNonNumericalChars(filterValue)) {
      return false;
    }

    const filter = parseInputNumber(filterValue)
      .map(value => formatDecimalPlaces(value, decimals))
      .getOrElse(null);

    const value = parseValueNumber(cellValue)
      .map(value => formatDecimalPlaces(value, decimals))
      .getOrElse(null);

    return filter != null && value != null ? fn(filter, value) : false;
  };

  return {
    allowedAggFuncs: ["avg", "median", "first", "last", "max", "min", "sum"],
    aggFunc: "sum",
    filter: "formattedNumberFilter",
    floatingFilterComponent: "proxyFloatingFilter",
    filterParams: {
      filterOptions: [
        {
          displayKey: "equals",
          displayName: translate("ag-grid.equals"),
          test: equalsTest(
            getter,
            filtering((filter, cell) => filter === cell)
          ),
        },
        {
          displayKey: "greaterThan",
          displayName: translate("ag-grid.greaterThan"),
          test: filtering((filter, cell) => cell > filter),
        },
        {
          displayKey: "greaterThanOrEqual",
          displayName: translate("ag-grid.greaterThanOrEqual"),
          test: filtering((filter, cell) => cell >= filter),
        },
        {
          displayKey: "lessThan",
          displayName: translate("ag-grid.lessThan"),
          test: filtering((filter, cell) => cell < filter),
        },
        {
          displayKey: "lessThanOrEqual",
          displayName: translate("ag-grid.lessThanOrEqual"),
          test: filtering((filter, cell) => cell <= filter),
        },
      ],
      defaultOption: "equals",
    },
    comparator: numberComparator(getter),
    valueGetter: params =>
      maybeEmpty(
        typeof getter(params) === "object"
          ? getter(params)
          : getter(params) === ""
          ? ""
          : escalaFormatacao(zeroToString(Number(getter(params))), escala)
      ).getOrElse(""),
    valueFormatter: params =>
      maybeEmpty(params.value)
        .map(parseFloat)
        .chain(onlyNumbers)
        .map(number => number.toString())
        .map(number =>
          formatNumber(
            {
              ...pick(["decimals", "prefix", "suffix"], col),
              decimals,
              prefix: maybeEmpty(moneySymbol(col.currency))
                .map(symbol => `${symbol} `)
                .getOrElse(null),
            },
            number
          )
        )
        .map(removePad)
        .getOrElse(
          removePad(
            typeof getter(params) !== "undefined" && isNaN(getter(params))
              ? getter(params)
              : formatNumber(
                  {
                    ...pick(["decimals", "prefix", "suffix"], col),
                    decimals,
                    prefix: maybeEmpty(moneySymbol(col.currency))
                      .map(symbol => `${symbol} `)
                      .getOrElse(null),
                  },
                  typeof getter(params) === "undefined"
                    ? params.value
                      ? 0
                      : ""
                    : getter(params)
                )
          )
        ),
  };
};

export const createPercentageColumn = (
  getter,
  col,
  decimalsDefault,
  translate
) => {
  const decimals =
    hasPath(["casasDecimais"], col) && !isNil(col.casasDecimais)
      ? col.casasDecimais
      : decimalsDefault;

  const filtering = fn => (filterValue, cellValue) => {
    if (hasNonNumericalChars(filterValue)) {
      return false;
    }

    const filter = parseInputNumber(filterValue)
      .map(value => formatDecimalPlaces(value, decimals))
      .getOrElse(null);

    const value = parseValueNumber(cellValue)
      .map(value => formatDecimalPlaces(value * 100, decimals))
      .getOrElse(null);

    return filter != null && value != null ? fn(filter, value) : false;
  };

  return {
    allowedAggFuncs: ["avg", "median", "first", "last", "max", "min"],
    aggFunc: "avg",
    filter: "formattedNumberFilter",
    floatingFilterComponent: "proxyFloatingFilter",
    filterParams: {
      filterOptions: [
        {
          displayKey: "equals",
          displayName: translate("ag-grid.equals"),
          test: equalsTest(
            getter,
            filtering((filter, cell) => filter === cell)
          ),
        },
        {
          displayKey: "greaterThan",
          displayName: translate("ag-grid.greaterThan"),
          test: filtering((filter, cell) => cell > filter),
        },
        {
          displayKey: "greaterThanOrEqual",
          displayName: translate("ag-grid.greaterThanOrEqual"),
          test: filtering((filter, cell) => cell >= filter),
        },
        {
          displayKey: "lessThan",
          displayName: translate("ag-grid.lessThan"),
          test: filtering((filter, cell) => cell < filter),
        },
        {
          displayKey: "lessThanOrEqual",
          displayName: translate("ag-grid.lessThanOrEqual"),
          test: filtering((filter, cell) => cell <= filter),
        },
      ],
      defaultOption: "equals",
    },
    comparator: numberComparator(getter),
    valueGetter: params =>
      maybeEmpty(
        typeof getter(params) === "object"
          ? zeroToString(getter(params))
          : getter(params) === ""
          ? ""
          : zeroToString(Number(getter(params)))
      ).getOrElse(""),
    valueFormatter: params =>
      maybeEmpty(params.value)
        .map(parseFloat)
        .chain(onlyNumbers)
        .map(number => String(new Big(number).times(100).toNumber()))
        .map(number =>
          formatNumber(
            {
              suffix: " %",
              ...pick(["decimals", "prefix", "suffix"], col),
              decimals,
            },
            number
          )
        )
        .getOrElse(
          typeof getter(params) !== "undefined" && isNaN(getter(params))
            ? getter(params)
            : formatNumber(
                {
                  suffix: " %",
                  ...pick(["decimals", "prefix", "suffix"], col),
                  decimals,
                },
                typeof getter(params) === "undefined"
                  ? params.value
                    ? 0
                    : ""
                  : getter(params)
              )
        ),
  };
};
